import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import {
  Firestore,
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  Unsubscribe,
  Timestamp,
} from '@angular/fire/firestore';
import { WebsiteService, Domain } from '../../services/website.service';
import { AuthService } from '../../services/auth.service';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { doc, getDoc } from '@angular/fire/firestore';
import { Subscription } from 'rxjs';

interface OrderRow {
  id: string;
  number?: string;
  status: string;
  total: number;
  currency: string;
  customerName: string;
  customerEmail: string;
  itemCount: number;
  dateCreated: Date | null;
  domain: string;
}

interface SubmissionRow {
  id: string;
  domain: string;
  formId?: string;
  name?: string;
  email?: string;
  preview: string;
  submittedAt: Date | null;
}

/** A single day of `reports/daily-{YYYY-MM-DD}`, narrowed to what the chart needs. */
interface ChartReport {
  date: string;
  exists: boolean;
  orderCount: number;
  revenue: number;
  submissionCount: number;
  // Per-domain split — used when domain filter is active
  byDomain?: Record<string, { orderCount: number; revenue: number; submissionCount: number }>;
  /**
   * Compact per-day chart data embedded in the monthly report.
   * Key: 'YYYY-MM-DD'. Populated only on monthSnapshot.
   */
  dailyBreakdown?: Record<string, { orderCount: number; revenue: number; byDomain: Record<string, { orderCount: number; revenue: number }> }>;
}

/** Local midnight at the start of today (browser timezone). */
function startOfToday(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

/**
 * Round `value` up to a "nice" ceiling for chart axes (e.g. 7 → 10, 23 → 25,
 * 87 → 100, 142 → 150). Keeps the y-axis labels readable.
 */
function niceCeiling(value: number): number {
  if (value <= 0) return 1;
  if (value <= 5) return 5;
  if (value <= 10) return 10;
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
  const normalized = value / magnitude;
  let niceNormalized: number;
  if (normalized <= 1) niceNormalized = 1;
  else if (normalized <= 2) niceNormalized = 2;
  else if (normalized <= 2.5) niceNormalized = 2.5;
  else if (normalized <= 5) niceNormalized = 5;
  else niceNormalized = 10;
  return niceNormalized * magnitude;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './dashboard.component.html',
})
export class DashboardComponent implements OnInit, OnDestroy {
  loadingOrders = true;
  loadingSubmissions = true;

  // Raw data (all domains)
  private allOrders: OrderRow[] = [];
  private allSubmissions: SubmissionRow[] = [];

  // Filtered, displayed data
  orders: OrderRow[] = [];
  submissions: SubmissionRow[] = [];

  // Domain filter
  domains: Domain[] = [];
  selectedDomain = ''; // empty = all domains

  // Today KPIs — derived from allOrders/allSubmissions (already loaded)
  // by filtering on dateCreated >= today. No extra Firestore read needed.
  todayOrderCount = 0;
  private todayLiveRevenue = 0;
  private todayLiveSubmissions = 0;

  // Month/week KPIs come from `reports/monthly-*` and `reports/weekly-*` +
  // today's live data on top. 2 extra Firestore reads on page load —
  // beats scanning the orders collection by orders of magnitude.
  monthRevenue = 0;
  weekSubmissionCount = 0;
  monthSnapshot: ChartReport | null = null;
  weekSnapshot: ChartReport | null = null;

  // Backfill: navigate by 7-day week. weekOffset 0 = last 7 days (yesterday
  // back to 6 days before that), 1 = the week before that, etc.
  weekOffset = 0;
  backfilling = false;
  backfillMessage = '';
  backfillTrace: string[] = [];

  // Restrict the backfill button to a single admin operator.
  private static readonly BACKFILL_ALLOWED_EMAIL = 'mvangastel@gmail.com';
  canBackfill = false;
  private authSub: Subscription | null = null;

  // Current-month daily reports for the chart
  chartReports: ChartReport[] = [];
  loadingChart = true;
  chartMaxOrders = 0;
  chartTotalOrders = 0;
  chartTotalRevenue = 0;
  chartTotalSubmissions = 0;


  private unsubOrders: Unsubscribe | null = null;
  private unsubSubmissions: Unsubscribe | null = null;

  constructor(
    private firestore: Firestore,
    private functions: Functions,
    private websiteService: WebsiteService,
    private authService: AuthService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.authSub = this.authService.currentUser$.subscribe((user) => {
      this.canBackfill = user?.email === DashboardComponent.BACKFILL_ALLOWED_EMAIL;
    });

    try {
      this.domains = await this.websiteService.getDomains();
    } catch (err) {
      console.error('Failed to load domains:', err);
    }
    this.subscribeOrders();
    this.subscribeSubmissions();
    this.loadPeriodSnapshots();
  }

  /**
   * Loads the current month + current ISO week snapshots from `reports/`.
   * Used to power the "Omzet deze maand" and "Berichten deze week" KPIs
   * AND to build the "Deze maand" bar chart (from monthSnapshot.dailyBreakdown).
   * Total: 2 Firestore reads for everything — KPIs + chart.
   */
  private async loadPeriodSnapshots(): Promise<void> {
    const now = new Date();
    const monthKey = this.formatNlYearMonth(now);
    const weekKey = this.formatIsoWeekKey(now);

    try {
      const [mSnap, wSnap] = await Promise.all([
        getDoc(doc(this.firestore, 'reports', `monthly-${monthKey}`)),
        getDoc(doc(this.firestore, 'reports', `weekly-${weekKey}`)),
      ]);
      this.monthSnapshot = mSnap.exists()
        ? (this.snapshotToChartReport(mSnap.data() as Record<string, unknown>))
        : null;
      this.weekSnapshot = wSnap.exists()
        ? (this.snapshotToChartReport(wSnap.data() as Record<string, unknown>))
        : null;
      this.recomputePeriodStats();
      this.buildChartFromMonthSnapshot();
    } catch (err) {
      console.error('Failed to load period snapshots:', err);
      this.loadingChart = false;
    }
  }

  /** Map a raw snapshot doc → narrowed ChartReport shape. */
  private snapshotToChartReport(data: Record<string, unknown>): ChartReport {
    const global = (data['global'] as Record<string, unknown>) || {};
    return {
      date: (data['key'] as string) || '',
      exists: true,
      orderCount: (global['orderCount'] as number) || 0,
      revenue: (global['revenue'] as number) || 0,
      submissionCount: (global['submissionCount'] as number) || 0,
      byDomain: (data['byDomain'] as ChartReport['byDomain']) || {},
      dailyBreakdown: (data['dailyBreakdown'] as ChartReport['dailyBreakdown']) || {},
    };
  }

  /**
   * Set month/week KPIs by combining the snapshot (up to yesterday) with
   * today's live data (top-up).
   */
  private recomputePeriodStats(): void {
    const dom = this.selectedDomain;

    const monthFromSnapshot = this.monthSnapshot
      ? this.scopedValue(this.monthSnapshot, dom, 'revenue')
      : 0;
    this.monthRevenue = Number((monthFromSnapshot + this.todayLiveRevenue).toFixed(2));

    const weekFromSnapshot = this.weekSnapshot
      ? this.scopedValue(this.weekSnapshot, dom, 'submissionCount')
      : 0;
    this.weekSubmissionCount = weekFromSnapshot + this.todayLiveSubmissions;
  }

  /**
   * Read a numeric field from a snapshot, scoped to a specific domain or
   * summed across `byDomain` for "all domains". Avoids relying on a possibly
   * missing top-level `global` field.
   */
  private scopedValue(
    snap: ChartReport,
    domain: string,
    field: 'revenue' | 'orderCount' | 'submissionCount',
  ): number {
    if (domain) {
      return snap.byDomain?.[domain]?.[field] ?? 0;
    }
    // "All domains" = sum across byDomain map
    return Object.values(snap.byDomain || {}).reduce(
      (sum, d) => sum + ((d[field] as number) || 0),
      0,
    );
  }

  /** 'YYYY-MM' in NL timezone. */
  private formatNlYearMonth(d: Date): string {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Amsterdam',
      year: 'numeric', month: '2-digit',
    }).format(d);
  }

  /** ISO 8601 week key for the NL calendar, e.g. '2026-W21'. */
  private formatIsoWeekKey(d: Date): string {
    const nlDate = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Amsterdam',
      year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(d);
    const [y, m, day] = nlDate.split('-').map(Number);
    const t = new Date(Date.UTC(y, m - 1, day));
    const dow = t.getUTCDay() || 7;
    t.setUTCDate(t.getUTCDate() + 4 - dow);
    const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
    const week = Math.ceil(((t.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
    return `${t.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
  }

  /**
   * Returns the 7-day range for the current weekOffset.
   * Offset 0 = yesterday back to 7 days ago (the most recent complete week).
   * Offset 1 = the week before that, etc.
   */
  private weekRange(offset: number): { start: Date; end: Date } {
    const today = new Date();
    const end = new Date(today.getTime() - offset * 7 * 24 * 60 * 60 * 1000 - 24 * 60 * 60 * 1000);
    const start = new Date(end.getTime() - 6 * 24 * 60 * 60 * 1000);
    return { start, end };
  }

  /** Display label for the currently-selected backfill week. */
  get weekRangeLabel(): string {
    const { start, end } = this.weekRange(this.weekOffset);
    const fmt = (d: Date) =>
      d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
    return `${fmt(start)} – ${fmt(end)}`;
  }

  prevWeek(): void {
    this.weekOffset += 1;
    this.backfillMessage = '';
  }

  nextWeek(): void {
    if (this.weekOffset > 0) {
      this.weekOffset -= 1;
      this.backfillMessage = '';
    }
  }

  /** Run backfill for the currently selected 7-day window. */
  async runBackfill(): Promise<void> {
    if (this.backfilling) return;
    this.backfilling = true;
    this.backfillMessage = '';

    const { start, end } = this.weekRange(this.weekOffset);
    const fmtDate = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    this.backfillTrace = [];

    try {
      const callable = httpsCallable<
        { startDate: string; endDate: string },
        {
          success: boolean;
          daysProcessed: number;
          daysFailed?: number;
          trace?: string[];
        }
      >(this.functions, 'backfillReports');

      const result = await callable({
        startDate: fmtDate(start),
        endDate: fmtDate(end),
      });

      this.backfillMessage =
        `${result.data.daysProcessed} dagen verwerkt` +
        (result.data.daysFailed ? ` (${result.data.daysFailed} mislukt)` : '');
      this.backfillTrace = result.data.trace || [];

      // Reload the monthly snapshot (which rebuilds the chart from dailyBreakdown)
      await this.loadPeriodSnapshots();
    } catch (err) {
      console.error('Backfill failed:', err);
      this.backfillMessage = 'Backfill mislukt: ' + (err as Error).message;
    } finally {
      this.backfilling = false;
      // Keep the trace visible — don't auto-clear it like the message
      setTimeout(() => (this.backfillMessage = ''), 8000);
    }
  }

  ngOnDestroy(): void {
    this.unsubOrders?.();
    this.unsubSubmissions?.();
    this.authSub?.unsubscribe();
  }

  /**
   * Build the bar chart for the current NL calendar month from
   * `monthSnapshot.dailyBreakdown`. Zero reads — data already loaded by
   * `loadPeriodSnapshots()`. Falls back to empty bars for days without a
   * daily report (e.g. gaps or today which has no report yet).
   */
  private buildChartFromMonthSnapshot(): void {
    const fmtNl = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Amsterdam',
      year: 'numeric', month: '2-digit', day: '2-digit',
    });
    const todayNl = fmtNl.format(new Date());
    const [year, month, todayDayStr] = todayNl.split('-');
    const todayDay = parseInt(todayDayStr, 10);

    const breakdown = this.monthSnapshot?.dailyBreakdown || {};
    const docs: ChartReport[] = [];

    for (let d = 1; d <= todayDay; d++) {
      const dayKey = `${year}-${month}-${String(d).padStart(2, '0')}`;
      const entry = breakdown[dayKey];
      if (entry) {
        docs.push({
          date: dayKey,
          exists: true,
          orderCount: entry.orderCount,
          revenue: entry.revenue,
          submissionCount: 0,
          byDomain: Object.fromEntries(
            Object.entries(entry.byDomain || {}).map(([dom, v]) => [
              dom,
              { orderCount: v.orderCount, revenue: v.revenue, submissionCount: 0 },
            ])
          ),
        });
      } else {
        docs.push({ date: dayKey, exists: false, orderCount: 0, revenue: 0, submissionCount: 0 });
      }
    }

    this.chartReports = docs;
    this.loadingChart = false;
    this.deriveChartStats();
  }

  /** Recompute aggregates + max, filtered by selectedDomain. */
  private deriveChartStats(): void {
    let orders = 0;
    let revenue = 0;
    let submissions = 0;
    let maxOrders = 0;
    const dom = this.selectedDomain;

    for (const r of this.chartReports) {
      const scope = dom
        ? r.byDomain?.[dom] || { orderCount: 0, revenue: 0, submissionCount: 0 }
        : { orderCount: r.orderCount, revenue: r.revenue, submissionCount: r.submissionCount };
      orders += scope.orderCount;
      revenue += scope.revenue;
      submissions += scope.submissionCount;
      if (scope.orderCount > maxOrders) maxOrders = scope.orderCount;
    }

    this.chartTotalOrders = orders;
    this.chartTotalRevenue = Number(revenue.toFixed(2));
    this.chartTotalSubmissions = submissions;
    this.chartMaxOrders = maxOrders;
  }

  /** Bar height percentage, relative to the rounded-up y-axis ceiling. */
  chartBarHeight(report: ChartReport): number {
    const max = this.chartMaxOrders;
    if (max === 0) return 0;
    const dom = this.selectedDomain;
    const count = dom
      ? report.byDomain?.[dom]?.orderCount ?? 0
      : report.orderCount;
    const niceMax = niceCeiling(max);
    return Math.round((count / niceMax) * 100);
  }

  chartTooltip(report: ChartReport): string {
    const dom = this.selectedDomain;
    const o = dom ? report.byDomain?.[dom]?.orderCount ?? 0 : report.orderCount;
    const r = dom ? report.byDomain?.[dom]?.revenue ?? 0 : report.revenue;
    const date = new Date(report.date + 'T12:00:00Z').toLocaleDateString('nl-NL', {
      day: 'numeric', month: 'short',
    });
    return `${date}: ${o} bestelling${o === 1 ? '' : 'en'} · ${this.formatCurrency(r)}`;
  }

  /** Short hover label shown above a bar. */
  chartHoverLabel(report: ChartReport): string {
    const dom = this.selectedDomain;
    const o = dom ? report.byDomain?.[dom]?.orderCount ?? 0 : report.orderCount;
    const r = dom ? report.byDomain?.[dom]?.revenue ?? 0 : report.revenue;
    const date = new Date(report.date + 'T12:00:00Z').toLocaleDateString('nl-NL', {
      day: 'numeric', month: 'short',
    });
    return `${date} · ${o} ${o === 1 ? 'bestelling' : 'bestellingen'} · ${this.formatCurrency(r)}`;
  }

  /** Compact day-of-month label for the x-axis (just the day number). */
  chartShortDate(report: ChartReport): string {
    // Parse the date part directly — avoids timezone-shift issues with new Date()
    return String(parseInt(report.date.split('-')[2], 10));
  }

  /** Tick labels for the y-axis (top → bottom: 100% → 0% of max). */
  get chartYTicks(): string[] {
    const max = this.chartMaxOrders;
    if (max === 0) return ['0'];
    // Round up to a nice step
    const niceMax = niceCeiling(max);
    return [niceMax, Math.round(niceMax * 0.75), Math.round(niceMax * 0.5), Math.round(niceMax * 0.25), 0].map(
      (n) => String(n),
    );
  }

  private subscribeOrders() {
    // De laatste 50 bestellingen op datum.
    const q = query(
      collection(this.firestore, 'orders'),
      orderBy('date_created', 'desc'),
      limit(50),
    );
    this.unsubOrders = onSnapshot(
      q,
      (snap) => {
        const parseDate = (v: unknown): Date | null => {
          if (!v) return null;
          if (v instanceof Timestamp) return v.toDate();
          if (v instanceof Date) return v;
          if (typeof v === 'string') {
            const d = new Date(v);
            return isNaN(d.getTime()) ? null : d;
          }
          if (typeof v === 'number') return new Date(v);
          return null;
        };

        this.allOrders = snap.docs.map((d) => {
          const data = d.data() as Record<string, unknown>;
          const billing = (data['billing'] as Record<string, unknown>) || {};
          const lineItems = Array.isArray(data['line_items']) ? data['line_items'] : [];

          const dateCreated =
            parseDate(data['date_created']) ||
            parseDate(data['date_created_gmt']) ||
            null;

          return {
            id: d.id,
            number: (data['number'] as string) || d.id,
            status: (data['status'] as string) || 'unknown',
            total: parseFloat((data['total'] as string) || '0') || 0,
            currency: (data['currency_symbol'] as string) || '€',
            customerName: `${billing['first_name'] || ''} ${billing['last_name'] || ''}`.trim() || '—',
            customerEmail: (billing['email'] as string) || '',
            itemCount: lineItems.length,
            dateCreated,
            domain: (data['domain'] as string) || (data['shop_domain'] as string) || 'dutchwatertech.net',
          };
        });

        // Sort newest-first by best available date.
        this.allOrders.sort((a, b) => {
          const at = a.dateCreated?.getTime() ?? 0;
          const bt = b.dateCreated?.getTime() ?? 0;
          return bt - at;
        });

        this.applyFilter();
        this.loadingOrders = false;
      },
      (err) => {
        console.error('orders subscription error:', err);
        this.loadingOrders = false;
      }
    );
  }

  private subscribeSubmissions() {
    // No orderBy: documents without `submittedAt` would be dropped otherwise.
    // Newest first — same reason as orders above.
    const q = query(
      collection(this.firestore, 'form_submissions'),
      orderBy('submittedAt', 'desc'),
      limit(50),
    );
    this.unsubSubmissions = onSnapshot(
      q,
      (snap) => {
        const all: SubmissionRow[] = snap.docs.map((d) => {
          const data = d.data() as Record<string, unknown>;
          const formData = (data['formData'] as Record<string, string | boolean>) || {};

          // Try common field names for name/email
          const name =
            (formData['naam'] as string) ||
            (formData['Naam'] as string) ||
            (formData['name'] as string) ||
            (formData['Name'] as string) ||
            '';
          const email =
            (formData['email'] as string) ||
            (formData['Email'] as string) ||
            (formData['e-mail'] as string) ||
            '';

          // Build preview: first text-like field that isn't name/email
          const preview = Object.entries(formData)
            .filter(([k, v]) => typeof v === 'string' && v && !['naam', 'Naam', 'name', 'Name', 'email', 'Email', 'e-mail'].includes(k))
            .map(([, v]) => v as string)
            .join(' · ')
            .slice(0, 120);

          let submittedAt: Date | null = null;
          const sa = data['submittedAt'];
          if (sa instanceof Timestamp) submittedAt = sa.toDate();
          else if (typeof sa === 'string') submittedAt = new Date(sa);

          return {
            id: d.id,
            domain: (data['domain'] as string) || '',
            formId: (data['formId'] as string) || '',
            name,
            email,
            preview: preview || '(geen inhoud)',
            submittedAt,
          };
        });

        // Sort newest-first; submissions without timestamp go to the back.
        all.sort((a, b) => {
          const at = a.submittedAt?.getTime() ?? 0;
          const bt = b.submittedAt?.getTime() ?? 0;
          return bt - at;
        });

        this.allSubmissions = all;
        this.applyFilter();
        this.loadingSubmissions = false;
      },
      (err) => {
        console.error('form_submissions subscription error:', err);
        this.loadingSubmissions = false;
      }
    );
  }

  onDomainChange(): void {
    this.applyFilter();   // also calls recomputePeriodStats internally
    this.deriveChartStats();
  }

  /**
   * Filter de displayed orders + submissions op het geselecteerde domein.
   * Today-count = orders uit allOrders waarvan dateCreated op vandaag valt
   * (browser-local datum, geen timezone-acrobatiek).
   */
  private applyFilter(): void {
    const dom = this.selectedDomain;
    const todayStart = startOfToday();

    const filteredOrders = dom
      ? this.allOrders.filter((o) => o.domain === dom)
      : this.allOrders;
    this.orders = filteredOrders.slice(0, 10);

    const todayOrders = filteredOrders.filter(
      (o) => o.dateCreated != null && o.dateCreated >= todayStart,
    );
    this.todayOrderCount = todayOrders.length;
    this.todayLiveRevenue = todayOrders
      .filter((o) => ['processing', 'completed', 'on-hold'].includes(o.status))
      .reduce((sum, o) => sum + o.total, 0);

    const filteredSubs = dom
      ? this.allSubmissions.filter((s) => s.domain === dom)
      : this.allSubmissions;
    this.submissions = filteredSubs.slice(0, 10);

    this.todayLiveSubmissions = filteredSubs.filter(
      (s) => s.submittedAt != null && s.submittedAt >= todayStart,
    ).length;

    this.recomputePeriodStats();
  }

  statusLabel(status: string): string {
    const map: Record<string, string> = {
      pending: 'Wacht op betaling',
      processing: 'In behandeling',
      'on-hold': 'In de wacht',
      completed: 'Voltooid',
      cancelled: 'Geannuleerd',
      refunded: 'Terugbetaald',
      failed: 'Mislukt',
    };
    return map[status] || status;
  }

  statusColor(status: string): string {
    const map: Record<string, string> = {
      pending: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30',
      processing: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
      'on-hold': 'bg-orange-500/15 text-orange-300 border-orange-500/30',
      completed: 'bg-green-500/15 text-green-300 border-green-500/30',
      cancelled: 'bg-gray-500/15 text-gray-300 border-gray-500/30',
      refunded: 'bg-purple-500/15 text-purple-300 border-purple-500/30',
      failed: 'bg-red-500/15 text-red-300 border-red-500/30',
    };
    return map[status] || 'bg-gray-500/15 text-gray-300 border-gray-500/30';
  }

  formatCurrency(amount: number, symbol = '€'): string {
    return `${symbol} ${amount.toFixed(2).replace('.', ',')}`;
  }

  formatDate(d: Date | null): string {
    if (!d) return '—';
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const isYesterday = d.toDateString() === yesterday.toDateString();

    const time = d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
    if (isToday) return `Vandaag ${time}`;
    if (isYesterday) return `Gisteren ${time}`;
    return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
  }
}
